import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InsightsWidgetHeader, ProfileInsightsStates } from '../components/insights/InsightsWidgetHeader';
import { NavigationProps } from '../components/insights/Navigation';
import { InsightsWidgetFooter } from '../components/insights/InsightsWidgetFooter';
import { DashboardContext } from '../contexts/DashboardContext';
import { HorizontalStackBar } from '../components/insights/HorizontalStackBar';
import { useStore } from '../../../store';
import { getStartTimeForPeriodType } from '../utils/insights.utils';
import { fetchNewInsightsByType, InsightType } from '../queryFunctions/fetchInsightsByType';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { WidgetLink } from '../components/WidgetLink';
import { VIEWS_MAP } from '../../../router/constants';
import { SingleBarChart } from '../components/insights/SingleBarChart';
import { ProfitInsightsWidgetSkeleton } from '../components/skeletons/ProfitInsightsWidgetSkeleton';
import bugsnagHandler from '../../../services/bugsnag.service';

interface IncomesInsightProps {
  isLoading: boolean;
  showHorizontalChart: boolean;
}

export enum ChartType {
  StackBar,
  Bars,
}

export const testIdPrefix = 'IncomesInsightsWidget';

export const IncomesInsight = ({ isLoading, showHorizontalChart }: IncomesInsightProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { incomeChartData, chartFilters } = useContext(DashboardContext);
  const { customerId } = useStore((state) => state.currUser());
  const { t } = useTranslation('dashboardFaceliftResourcesEN');
  const [periodType, setPeriodType] = useState<ProfileInsightsStates>(ProfileInsightsStates.Yearly);
  const [subPeriodType, setSubPeriodType] = useState<string>((new Date()).getFullYear().toString());
  const [shouldUseInitialData, setShouldUseInitialData] = useState<boolean>(true);
  const [isLoadingNewData, setIsLoadingNewData] = useState<boolean>(false);
  const [newFetchedData, setNewFetchedData] = useState<any>();
  const incomeInsightsMainData = shouldUseInitialData ? incomeChartData : newFetchedData?.data;
  const isHorizontalChartDataInvalid = !incomeInsightsMainData?.categoriesItems?.length && incomeInsightsMainData?.chartSummaryData?.total > 0;
  const [chartType, setChartType] = useState<ChartType>(ChartType.Bars);

  const [navOptions, setNavOptions] = useState<NavigationProps>({
    isNext: false,
    isPrevious: false,
    onNext: () => false,
    onPrevious: () => false,
  });

  useEffect(() => {
    setChartType(isHorizontalChartDataInvalid ? ChartType.Bars : showHorizontalChart ? ChartType.StackBar : ChartType.Bars);
  }, [incomeInsightsMainData]);

  useEffect(() => {
    trackMixPanel('Act on Income Widget', { Action: periodType });
    trackMixPanel('Act on Income Widget', { Action: subPeriodType });

    if (chartType === ChartType.StackBar) {
      trackMixPanel('Act on Income Widget', { WidgetView: 'Summary View' });
    }

    if (chartType === ChartType.Bars) {
      trackMixPanel('Act on Income Widget', { WidgetView: 'Timeline View' });
    }
  }, [periodType, subPeriodType, chartType]);

  useEffect(() => {
    if (chartType === ChartType.StackBar) {
      setNavOptions({
        isNext: true,
        isPrevious: false,
        onNext: () => {
          setChartType(ChartType.Bars);
          trackMixPanel('Act on Income Widget', { Action: 'Arrow Right' });
        },
        onPrevious: () => false,
      });
    }
    if (chartType === ChartType.Bars) {
      setNavOptions({
        isNext: false,
        isPrevious: showHorizontalChart && !isHorizontalChartDataInvalid,
        onNext: () => false,
        onPrevious: () => {
          setChartType(ChartType.StackBar);
          trackMixPanel('Act on Income Widget', { Action: 'Arrow Left' });
        },
      });
    }
  }, [periodType, subPeriodType, chartType]);

  const handleFilterChange = (state: ProfileInsightsStates, subOption: string) => {
    const startTime = getStartTimeForPeriodType(chartFilters, state, subOption);
    if (startTime) {
      setIsLoadingNewData(true);
      fetchNewInsightsByType(customerId, state, startTime, InsightType.Income)
        .then((data: any) => {
          if (shouldUseInitialData) setShouldUseInitialData(false);
          setPeriodType(state);
          setSubPeriodType(subOption);
          setNewFetchedData(data);
        })
        .catch((e) => {
          bugsnagHandler.notify(
            `FetchInsightsByType error message: ${e?.message} and props: customerId: ${customerId} , periodType: ${state}, startTime: ${startTime}, insightType: ${InsightType.Income}`,
          );
        })
        .finally(() => {
          setIsLoadingNewData(false);
        });
    }
  };

  if (isLoading || isLoadingNewData) return <ProfitInsightsWidgetSkeleton />;

  const renderActionButton = () => {
    const handleAllExpenses = () => {
      trackMixPanel('Act on Expenses Widget', { Action: 'All Expenses' });
    };

    return (
      <div className='flex'>
        <WidgetLink
          onClick={handleAllExpenses}
          linkTitle={t('WIDGET_LATEST_TRANSACTIONS.BUTTON')}
          linkUrl={`/${VIEWS_MAP.TRANSACTIONS}`}
          testId={`${testIdPrefix}_manage-link`}
        />
      </div>
    );
  };
  return (
    <div
      className='flex flex-col bg-bw00 justify-between rounded-[12px] p-[19px] pb-[13px] h-full'
      data-testid={`${testIdPrefix}__wrapper`}
    >
      <InsightsWidgetHeader
        title={t('INSIGHT_WIDGET.INCOMES_TITLE')}
        chartSummaryData={incomeInsightsMainData?.chartSummaryData}
        onStateChange={handleFilterChange}
        showChartIndex={false}
        chartFilters={chartFilters}
        periodType={periodType}
        subPeriodType={subPeriodType}
        isMonthlyPeriodDisabled={chartType === ChartType.Bars}
      />
      <div data-testid={`${testIdPrefix}__GraphsWrapper`}>
        {chartType === ChartType.StackBar && showHorizontalChart && (
          <HorizontalStackBar incomeChartData={incomeInsightsMainData} />
        )}
        {chartType === ChartType.Bars && (
          <SingleBarChart
            segmentedItems={incomeInsightsMainData?.segmentedItems}
            originInsightType={InsightType.Income}
          />
        )}
        <InsightsWidgetFooter
          {...navOptions}
          rightActionButton={renderActionButton()}
          showNavigation={!showHorizontalChart}
        />
      </div>
    </div>
  );
};
